import type { AttemptResult, FurthestStep, PortalOverview, ShieldAnswer, SourcesFilters, WhoBucket } from "@/types";
import { countryName } from "@/utils/countryName.ts";
import { formatAsn } from "@/utils/formatAsn.ts";
import { ANSWER_ORDER, FURTHEST_STEP_ORDER, LISTED_GROUPS, WHO_ORDER } from "@/utils/sourcesFilters.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

export interface FilterOption {
  readonly value: string;
  readonly label: string;
}

export interface SourcesChip {
  readonly id: string;
  readonly label: string;
  readonly without: SourcesFilters;
}

type ListKey = "groups" | "rules" | "furthestSteps" | "answers" | "attempts" | "who" | "countries" | "networks";
type FlagKey = "yourProduct" | "named" | "namedLater";

export const STEP_OPTIONS: readonly FilterOption[] = FURTHEST_STEP_ORDER.map((key) => ({ value: key, label: copy.STEPS[key] }));
export const ANSWER_OPTIONS: readonly FilterOption[] = ANSWER_ORDER.map((key) => ({ value: key, label: copy.ANSWERS[key] }));
export const WHO_OPTIONS: readonly FilterOption[] = WHO_ORDER.map((key) => ({ value: key, label: copy.WHO[key] }));

const LIST_CHIPS: readonly { readonly key: ListKey; readonly name: string; readonly label: (value: string) => string }[] = [
  { key: "groups", name: copy.FILTER_LOOKED_FOR, label: copy.groupLabel },
  { key: "rules", name: copy.COL_RULE, label: (value) => copy.ruleName(value, null) },
  { key: "furthestSteps", name: copy.FILTER_STEP, label: (value) => copy.STEPS[value as FurthestStep] },
  { key: "answers", name: copy.FILTER_ANSWER, label: (value) => copy.ANSWERS[value as ShieldAnswer] },
  { key: "attempts", name: copy.FIG_ATTEMPTS, label: (value) => copy.ATTEMPT_RESULTS[value as AttemptResult] },
  { key: "who", name: copy.FILTER_WHO, label: (value) => copy.WHO[value as WhoBucket] },
  { key: "countries", name: copy.FILTER_COUNTRY, label: countryName },
  { key: "networks", name: copy.FILTER_NETWORK, label: (value) => value },
];
const FLAG_CHIPS: readonly { readonly key: FlagKey; readonly label: string }[] = [
  { key: "yourProduct", label: copy.PRESET_YOUR_PRODUCT },
  { key: "named", label: copy.FIG_NAMED },
  { key: "namedLater", label: copy.NAMED_LATER },
];

export interface PortalWindow {
  readonly windowDays: number | null;
  readonly linesKnown: boolean;
}

/** The window's length, and whether the company has request lines in it. Nothing is known without the overview. */
export function readPortalWindow(overview: PortalOverview | undefined): PortalWindow {
  const coverage = overview?.available ? overview.coverage : null;
  return { windowDays: coverage?.window_days ?? null, linesKnown: (coverage?.days_with_lines ?? 0) > 0 };
}

function withCount(label: string, addresses: number): string {
  return `${label} (${addresses.toLocaleString("en-US")})`;
}

export function groupOptions(overview: PortalOverview | undefined): FilterOption[] {
  const keys = overview?.groups?.map((group) => group.group) ?? LISTED_GROUPS;
  return keys.map((key) => ({ value: key, label: copy.groupLabel(key) }));
}

export function countryOptions(overview: PortalOverview | undefined): FilterOption[] {
  return (overview?.countries ?? []).map((c) => ({ value: c.country_code, label: withCount(countryName(c.country_code), c.addresses) }));
}

export function networkOptions(overview: PortalOverview | undefined): FilterOption[] {
  return (overview?.networks ?? []).map((n) => ({ value: n.asn, label: withCount(formatAsn(n.asn, n.as_name) ?? n.asn, n.addresses) }));
}

function listChips(filters: SourcesFilters): SourcesChip[] {
  return LIST_CHIPS.flatMap(({ key, name, label }) => {
    const values: readonly string[] = filters[key];
    return values.map((value) => ({
      id: `${key}:${value}`,
      label: copy.chipLabel(name, label(value)),
      without: { ...filters, [key]: values.filter((other) => other !== value) } as SourcesFilters,
    }));
  });
}

function flagChips(filters: SourcesFilters): SourcesChip[] {
  return FLAG_CHIPS.filter(({ key }) => filters[key]).map(({ key, label }) => ({
    id: key,
    label,
    without: { ...filters, [key]: false } as SourcesFilters,
  }));
}

/** One removable chip per applied filter value, in the sidebar's order. */
export function buildSourcesChips(filters: SourcesFilters, windowDays: number | null): SourcesChip[] {
  const search = filters.search
    ? [{ id: "search", label: copy.chipLabel(copy.SEARCH_LABEL, filters.search), without: { ...filters, search: "" } }]
    : [];
  const firstSeen = filters.firstSeenDays === null
    ? []
    : [{
      id: "firstSeen",
      label: copy.chipLabel(copy.FILTER_FIRST_SEEN, copy.firstSeenLabel(filters.firstSeenDays, windowDays)),
      without: { ...filters, firstSeenDays: null },
    }];
  return [...search, ...listChips(filters), ...firstSeen, ...flagChips(filters)];
}
